import { getCategoryUnreadCount } from 'api/socket.request'
import { ChatStatus } from 'interfaces/talkplus.interface'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { userState } from 'store/user'
import { EventData, EventTypeEnum } from 'talkplus-sdk'
import customDebounce from 'utils/customDebounce'
import { useTalkPlusClient } from './TalkPlusClientContext'

type CategoryUnreadCountState = Record<ChatStatus, number> & { total: number }

// Context 타입 정의
interface CategoryUnreadCountContextType {
    categoryUnreadCount: CategoryUnreadCountState
    debouncedFetchUnreadCounts: () => Promise<void>
}

// Context를 생성합니다.
const CateogryUnreadCountContext =
    createContext<CategoryUnreadCountContextType | undefined>(undefined)

const CategoryUnreadCountProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const { client, isTalkplusLoginComplete } = useTalkPlusClient()
    const user = useRecoilValue(userState)

    const [categoryUnreadCount, setCategoryUnreadCount] = useState<CategoryUnreadCountState>({
        [ChatStatus.PENDING]: 0,
        [ChatStatus.IN_PROGRESS]: 0,
        [ChatStatus.COMPLETED]: 0,
        total: 0,
    })

    /**
     * 특정 카테고리에 대한 읽지 않은 메시지 수를 가져와 상태를 업데이트합니다.
     */
    const fetchUnreadCounts = useCallback(async () => {
        if (!client) return

        try {
            const updatedCounts = {} as Record<ChatStatus, number>

            const inProgressRes = await getCategoryUnreadCount(ChatStatus.IN_PROGRESS)
            const pendingRes = await getCategoryUnreadCount(ChatStatus.PENDING)

            updatedCounts[ChatStatus.IN_PROGRESS] = inProgressRes?.data?.data?.count || 0
            updatedCounts[ChatStatus.PENDING] = pendingRes?.data?.data?.count || 0

            const totalCount = Object.values(updatedCounts).reduce((sum, count) => sum + count, 0)

            setCategoryUnreadCount({
                ...updatedCounts,
                [ChatStatus.COMPLETED]: 0,
                total: totalCount,
            })
        } catch (error) {
            console.error('읽지 않은 메시지 수를 가져오는 데 실패했습니다:', error)
        }
    }, [client])

    const debouncedFetchUnreadCounts = customDebounce(fetchUnreadCounts, 1000)

    /**
     * 로그인 완료 시 초기 데이터를 가져옵니다.
     */
    useEffect(() => {
        if (isTalkplusLoginComplete) fetchUnreadCounts()
    }, [isTalkplusLoginComplete, fetchUnreadCounts, user])

    useEffect(() => {
        const handleEvent = (data: EventData) => {
            if (data.type === EventTypeEnum.Message) {
                debouncedFetchUnreadCounts()
            }
        }

        client.on('event', handleEvent)

        return () => {
            client?.off('event', handleEvent)
        }
    }, [client])

    return (
        <CateogryUnreadCountContext.Provider
            value={{ categoryUnreadCount, debouncedFetchUnreadCounts }}
        >
            {children}
        </CateogryUnreadCountContext.Provider>
    )
}

// Context를 사용하는 커스텀 훅을 만듭니다.
export const useCategoryUnreadCount = (): CategoryUnreadCountContextType => {
    const context = useContext(CateogryUnreadCountContext)
    if (!context) {
        throw new Error('provider가 존재해야 함')
    }
    return context
}

export default CategoryUnreadCountProvider
